import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import { Button, useToast } from '@org/ui';
import { BarChart3, Check, Clock, Users } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface PollOption {
  text: string;
  voteCount: number;
}

interface PollProps {
  postId: string;
}

export function Poll({ postId }: PollProps) {
  const { t } = useTranslation('community');
  const { toast } = useToast();
  const [selectedOption, setSelectedOption] = useState<number | null>(null);

  const { data: poll, isLoading: pollLoading, refetch: refetchPoll } =
    communityTrpc.polls.getByPost.useQuery({ postId });

  const { data: myVote, refetch: refetchMyVote } =
    communityTrpc.polls.getMyVote.useQuery(
      { pollId: poll?.pollId ?? '' },
      { enabled: !!poll?.pollId }
    );

  const { data: results, refetch: refetchResults } =
    communityTrpc.polls.getResults.useQuery(
      { pollId: poll?.pollId ?? '' },
      { enabled: !!poll?.pollId && (myVote?.hasVoted || poll?.hasEnded) }
    );

  const vote = communityTrpc.polls.vote.useMutation({
    onSuccess: () => {
      toast({ title: t('poll.voteRecorded') });
      refetchPoll();
      refetchMyVote();
      refetchResults();
    },
    onError: (error) => {
      toast({ title: t('common.error'), description: error.message, variant: 'destructive' });
    },
  });

  if (pollLoading) {
    return (
      <div className="mt-4 animate-pulse rounded-lg border bg-muted/50 p-4">
        <div className="h-5 w-3/4 rounded bg-muted" />
        <div className="mt-4 space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-10 rounded bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (!poll) {
    return null;
  }

  const options = poll.options as PollOption[];
  const hasVoted = myVote?.hasVoted ?? false;
  const userVotedOption = myVote?.optionIndex;
  const showResults = hasVoted || poll.hasEnded;

  const handleVote = () => {
    if (selectedOption === null) {
      toast({ title: t('poll.selectOption'), variant: 'destructive' });
      return;
    }
    vote.mutate({ pollId: poll.pollId, optionIndex: selectedOption });
  };

  const handleChangeVote = (optionIndex: number) => {
    if (!poll.hasEnded) {
      vote.mutate({ pollId: poll.pollId, optionIndex });
    }
  };

  return (
    <div className="mt-4 rounded-lg border bg-card p-4">
      {/* Poll Question */}
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-primary" />
          <h4 className="font-medium">{poll.question}</h4>
        </div>
        {poll.hasEnded && (
          <span className="rounded-full bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">
            {t('poll.ended')}
          </span>
        )}
      </div>

      {/* Options */}
      <div className="space-y-2">
        {options.map((option, index) => {
          const result = results?.results?.find((r) => r.index === index);
          const percentage = result?.percentage ?? 0;
          const voteCount = result?.voteCount ?? option.voteCount;
          const isSelected = selectedOption === index;
          const isUserVote = userVotedOption === index;

          if (showResults) {
            return (
              <button
                key={index}
                onClick={() => !poll.hasEnded && handleChangeVote(index)}
                disabled={poll.hasEnded || vote.isPending}
                className={`relative w-full overflow-hidden rounded-lg border p-3 text-left transition-colors ${
                  isUserVote
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-border/80'
                } ${poll.hasEnded ? 'cursor-default' : 'cursor-pointer'}`}
              >
                <div
                  className={`absolute inset-y-0 left-0 transition-all ${
                    isUserVote ? 'bg-primary/10' : 'bg-muted'
                  }`}
                  style={{ width: `${percentage}%` }}
                />

                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isUserVote && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                    <span className={isUserVote ? 'font-medium' : ''}>
                      {option.text}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {t('poll.votes', { count: voteCount })}
                    </span>
                    <span className="font-medium">{percentage}%</span>
                  </div>
                </div>
              </button>
            );
          }

          return (
            <button
              key={index}
              onClick={() => setSelectedOption(index)}
              className={`w-full rounded-lg border p-3 text-left transition-colors ${
                isSelected
                  ? 'border-primary bg-primary/5 ring-1 ring-primary'
                  : 'border-border hover:border-border/80 hover:bg-muted/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                    isSelected
                      ? 'border-primary bg-primary'
                      : 'border-muted-foreground/40'
                  }`}
                >
                  {isSelected && <Check className="h-3 w-3 text-white" />}
                </div>
                <span>{option.text}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Vote Button / Footer */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Users className="h-4 w-4" />
            {t('poll.votes', { count: poll.totalVotes })}
          </span>
          {poll.endsAt && !poll.hasEnded && (
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {t('poll.ends')} {formatDistanceToNow(new Date(poll.endsAt), { addSuffix: true })}
            </span>
          )}
        </div>

        {!showResults && !poll.hasEnded && (
          <Button
            onClick={handleVote}
            disabled={selectedOption === null || vote.isPending}
            size="sm"
          >
            {vote.isPending ? t('poll.voting') : t('poll.vote')}
          </Button>
        )}

        {showResults && !poll.hasEnded && (
          <span className="text-sm text-muted-foreground">
            {t('poll.changeVoteHint')}
          </span>
        )}
      </div>
    </div>
  );
}
